/**
 * Passenger profile localStorage cache — lets a returning passenger skip the
 * splash-screen wait for the first network round trip. Mirrors the driver
 * app's cache (DriverContext.js) exactly. Pure localStorage: no network
 * calls, no added cost.
 *
 * Extracted out of AppContext.js (which pulls in Firebase/Capacitor and
 * isn't importable from a plain Node test) so this can be unit tested.
 */
export const PASSENGER_PROFILE_CACHE_KEY = 'mt_passenger_profile_v1';

// A returning passenger whose cached profile was authoritatively confirmed
// (not blocked) within this window skips the splash screen immediately, while
// the server re-check runs in the background. Anything older or blocked waits
// for the server exactly as before. Every ride/payment action stays
// server-validated regardless — this only controls how fast the screen paints.
export const OPTIMISTIC_PROFILE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const readPassengerCacheRecord = () => {
  try {
    const raw = localStorage.getItem(PASSENGER_PROFILE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Cached profile for `uid`, or whatever is cached if `uid` is falsy. Never a mismatched uid's. */
export const getCachedUserProfile = (uid) => {
  try {
    const parsed = readPassengerCacheRecord();
    const profile = parsed?.profile;
    if (!profile?.id) return null;
    if (uid && profile.id !== uid) return null;
    return profile;
  } catch {
    return null;
  }
};

export const getCachedUserRecord = (uid) => {
  const parsed = readPassengerCacheRecord();
  const profile = parsed?.profile;
  if (!profile?.id) return null;
  if (uid && profile.id !== uid) return null;
  return { profile, cachedAt: Number(parsed.cachedAt) || 0, verifiedAt: Number(parsed.verifiedAt) || 0 };
};

// Safe to skip the splash screen from this cached record before the server
// answers? Only for a not-blocked passenger confirmed very recently.
export const canUnlockFromCache = (record) => Boolean(
  record
  && record.profile?.isBlocked !== true
  && record.verifiedAt > 0
  && (Date.now() - record.verifiedAt) < OPTIMISTIC_PROFILE_MAX_AGE_MS,
);

export const setCachedUserProfile = (profile, { verified = false } = {}) => {
  try {
    if (profile?.id) {
      const prev = readPassengerCacheRecord();
      const prevVerifiedAt = prev?.profile?.id === profile.id ? Number(prev.verifiedAt) || 0 : 0;
      localStorage.setItem(PASSENGER_PROFILE_CACHE_KEY, JSON.stringify({
        uid: profile.id,
        cachedAt: Date.now(),
        // Only bumped on an authoritative server confirmation — never on a
        // local Firestore cache echo — since this is what the splash-skip
        // gates on.
        verifiedAt: verified ? Date.now() : prevVerifiedAt,
        profile,
      }));
    }
  } catch { /* */ }
};

export const clearCachedUserProfile = () => {
  try { localStorage.removeItem(PASSENGER_PROFILE_CACHE_KEY); } catch { /* */ }
};
